import { useState, useEffect, type ComponentType } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { toast } from 'sonner';
import { assetService, type AssetWithPermission } from '@/services/asset-service';
import { canUserPerformAction } from '@/lib/asset-permission-utils';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Button } from '@/components/ui/button';
import { Users, ArrowLeft, Maximize, Minimize, Server } from 'lucide-react';
import { AssetSharingPanel } from './asset-sharing-panel';
import { AssetShareModal } from './asset-share-modal';
import { getEditorsForAssetType, type EditorConfig } from '@/config/editor-registry';
import { AssetPreview } from './asset-preview';
import { AssetRequestAccess } from './asset-request-access';
import AssetDetailsPanel from './asset-details-panel';

// AssetConsoleView: specifically for viewing/editing a single asset in the console (console/assets/:assetId/:tab?)
export const AssetConsoleView = () => {
  const { assetId, tab } = useParams<{ assetId: string; tab?: string }>();
  const navigate = useNavigate();
  const [asset, setAsset] = useState<AssetWithPermission | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<{ message: string; code?: number; assetName?: string } | null>(null);
  const [shareModalOpen, setShareModalOpen] = useState(false);
  const [availableTabs, setAvailableTabs] = useState<EditorConfig[]>([]);
  const [activeTab, setActiveTab] = useState<string>(tab || 'preview');
  const [isFullScreen, setIsFullScreen] = useState(false);

  // Load asset data and editor configurations
  useEffect(() => {
    const fetchAsset = async () => {
      if (assetId) {
        try {
          setLoading(true);
          setError(null);
          const fetchedAsset = await assetService.getAsset(assetId);

          // Enhance with mock permission data (in real implementation, backend would return this)
          const assetWithPermission = {
            ...fetchedAsset,
            permission: {
              role: 'owner', // This would be determined by backend
              canRead: true,
              canUpdate: true,
              canDelete: true,
              canShare: true
            }
          };

          setAsset(assetWithPermission as AssetWithPermission);

          // Load editor tabs based on asset type
          const tabs = getEditorsForAssetType(fetchedAsset.type);
          setAvailableTabs(tabs);

          // Set the active tab from URL if provided, otherwise default to 'preview'
          if (!tab) {
            const previewTab = tabs.find(t => t.id === 'preview');
            setActiveTab(previewTab ? 'preview' : tabs[0]?.id || 'preview');
          }
          // If tab is provided in URL, it's already set in initial state, so don't override
        } catch (error) {
          const errorMessage = error instanceof Error ? error.message : 'Unknown error';

          // Check if it's a permission/access error
          // Backend can return "access denied", "no permission", "forbidden", "unauthorized", etc.
          const isAccessDenied = errorMessage.toLowerCase().includes('access denied') ||
                                  errorMessage.toLowerCase().includes('no permission') ||
                                  errorMessage.toLowerCase().includes('forbidden') ||
                                  errorMessage.toLowerCase().includes('unauthorized');

          if (isAccessDenied) {
            // Show request access page instead of navigating away
            setError({
              message: errorMessage,
              code: 403,
            });
          } else {
            // For other errors, show toast and navigate
            toast.error(`Failed to load asset: ${errorMessage}`);
            navigate('/console/assets');
          }
        } finally {
          setLoading(false);
        }
      } else {
        setLoading(false);
      }
    };

    fetchAsset();
  }, [assetId, navigate]);

  // Sync active tab with URL parameter (only on mount or when URL changes externally)
  useEffect(() => {
    if (tab && tab !== activeTab) {
      setActiveTab(tab);
    }
  }, [tab]);

  // Handle tab change - just update state, don't navigate
  const handleTabChange = (newTab: string) => {
    setActiveTab(newTab);
    // Update URL without navigation to keep it shareable
    window.history.replaceState(null, '', `/console/assets/${assetId}/${newTab}`);
  };

  const renderEditorTabContent = (tab: EditorConfig) => {
    if (!assetId || !asset) return null;

    const resolvedProps = tab.getProps?.({
      assetId,
      assetType: asset.type,
    });

    const additionalProps = (
      resolvedProps && typeof resolvedProps === 'object'
        ? Object.fromEntries(
            Object.entries(resolvedProps).filter(([key]) => key !== 'assetId')
          )
        : {}
    ) as Record<string, unknown>;

    const TabComponent = tab.component as ComponentType<{
      assetId: string;
    } & Record<string, unknown>>;

    return (
      <div className="flex-1 overflow-hidden h-screen">
        <TabComponent assetId={assetId} {...additionalProps} />
      </div>
    );
  };

  // If loading, show a loading state
  if (loading) {
    return (
      <div className="container mx-auto py-8 flex justify-center items-center">
        <div className="text-center">
          <div className="inline-block animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500 mb-4"></div>
          <p>Loading asset...</p>
        </div>
      </div>
    );
  }

  // If there's an error (like access denied), show request access page
  if (error) {
    return (
      <div className="container mx-auto py-8">
        <AssetRequestAccess
          assetId={assetId}
          assetName={error.assetName}
        />
      </div>
    );
  }

  // If there's an assetId and the asset is loaded, render the asset details
  if (assetId && asset) {
    // Full-screen mode
    if (isFullScreen) {
      const currentTab = availableTabs.find(t => t.id === activeTab);

      return (
        <div className="fixed inset-0 z-50 bg-background flex flex-col">
          <div className="flex items-center justify-between p-4 border-b bg-background">
            <div className="flex items-center gap-3">
              <h1 className="text-xl font-bold">{asset.name}</h1>
              {currentTab && <span className="text-sm text-muted-foreground">/ {currentTab.label}</span>}
            </div>
            <Button
              variant="outline"
              size="sm"
              onClick={() => setIsFullScreen(false)}
              className="flex items-center"
            >
              <Minimize className="mr-2 h-4 w-4" />
              Exit Full Screen
            </Button>
          </div>
          <div className="flex-1 overflow-hidden">
            {currentTab ? renderEditorTabContent(currentTab) : (
              activeTab === 'preview' ? <AssetPreview asset={asset} assetId={assetId} /> : null
            )}
          </div>
        </div>
      );
    }

    // Normal mode with tabs
    return (
      <div className="container mx-auto py-8">
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center mb-6 gap-4">
          <div className="flex items-center gap-3">
            <Button
              variant="outline"
              size="sm"
              onClick={() => navigate('/console/assets')}
              className="flex items-center"
            >
              <ArrowLeft className="mr-2 h-4 w-4" />
              Back to Assets
            </Button>
            <h1 className="text-2xl font-bold">{asset.name}</h1>
          </div>
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => setIsFullScreen(true)}
              className="flex items-center"
            >
              <Maximize className="mr-2 h-4 w-4" />
              Full Screen
            </Button>
            {asset.permission && canUserPerformAction(asset.permission?.role, 'share') && (
              <Button variant="outline" onClick={() => setShareModalOpen(true)}>
                <Users className="mr-2 h-4 w-4" />
                Share Asset
              </Button>
            )}
            {asset.permission && canUserPerformAction(asset.permission?.role, 'share') && (
              <Button 
                variant="outline" 
                onClick={() => {
                  // Navigate to services management page with this asset pre-selected
                  navigate(`/console/services?assetId=${assetId}`);
                }}
              >
                <Server className="mr-2 h-4 w-4" />
                Publish as Service
              </Button>
            )}
          </div>
        </div>

        <Tabs value={activeTab} onValueChange={handleTabChange} className="space-y-4">
          <TabsList>
            <TabsTrigger value="preview">Preview</TabsTrigger>
            {/* Render editor tabs dynamically based on asset type */}
            {availableTabs
              .filter(tab => tab.id !== 'preview') // Skip preview as it's already added separately
              .map((tab) => (
                <TabsTrigger key={tab.id} value={tab.id}>
                  {tab.label}
                </TabsTrigger>
              ))}
            {asset.permission && canUserPerformAction(asset.permission.role, 'update') && (
              <TabsTrigger value="details">Details</TabsTrigger>
            )}
            <TabsTrigger value="sharing">Sharing</TabsTrigger>
          </TabsList>


          {availableTabs
            .map((tab) => (
              <TabsContent key={tab.id} value={tab.id} className="space-y-4">
                <div className="bg-white rounded-lg shadow p-6">
                  {renderEditorTabContent(tab)}
                </div>
              </TabsContent>
            ))}

          {asset.permission && canUserPerformAction(asset.permission.role, 'update') && (
            <TabsContent value="details">
              <AssetDetailsPanel
                asset={asset}
                onAssetUpdated={(updated) => setAsset({
                  ...updated,
                  permission: asset.permission
                })}
              />
            </TabsContent>
          )}

          <TabsContent value="sharing">
            <AssetSharingPanel
              asset={asset}
              onShareUpdate={() => {
                // Optionally refresh asset sharing data
              }}
            />
          </TabsContent>
        </Tabs>

        {shareModalOpen && asset && (
          <AssetShareModal
            asset={asset}
            isOpen={shareModalOpen}
            onClose={() => setShareModalOpen(false)}
            onShareComplete={() => {
              // Optionally refresh asset sharing data
            }}
          />
        )}
      </div>
    );
  }

  // If no asset is loaded despite having assetId, return empty
  return <div />;
};

// Keep original export for compatibility with existing routing in App.tsx
export default AssetConsoleView;
